import React, { useState, useEffect, useMemo } from 'react';
import { Search, Star, StarOff } from 'lucide-react';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useApp } from './AppContext';
import type { Question } from './AppContext';
import type { Screen } from '../App';

interface SearchResultsProps {
  results: Question[];
  query: string;
  onQuestionSelect: (question: Question) => void;
  onNavigate: (screen: Screen) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({ 
  results, 
  query, 
  onQuestionSelect, 
  onNavigate 
}) => {
  const { favorites, toggleFavorite, settings } = useApp();
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [currentExplanationQuestion, setCurrentExplanationQuestion] = useState<Question | null>(null);
  const [closingQuestion, setClosingQuestion] = useState<string | null>(null);

  // Мемоизированные расчёты размеров шрифтов
  const actualQuestionSize = useMemo(() => {
    const baseSize = 21;
    return Math.round((settings.questionFontSize / 50) * baseSize);
  }, [settings.questionFontSize]);
  
  const actualAnswerSize = useMemo(() => {
    const baseSize = 18;
    return Math.round((settings.answerFontSize / 50) * baseSize);
  }, [settings.answerFontSize]);

  // Сброс всех выбранных ответов при размонтировании компонента
  useEffect(() => {
    return () => {
      setSelectedAnswers({});
      setSelectedQuestion(null);
      setShowExplanation(false);
      setCurrentExplanationQuestion(null);
    };
  }, []);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ПДД':
        return 'glass-badge-success';
      case 'Дорожные знаки':
        return 'glass-badge-warning';
      case 'Безопасность':
        return 'glass-badge-danger';
      case 'Медицина':
        return 'glass-badge-default';
      default:
        return 'glass-badge-default';
    }
  };

  const toggleQuestionDetails = (questionId: string) => {
    if (selectedQuestion === questionId) {
      setClosingQuestion(questionId);
      
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: undefined
      }));
      
      setTimeout(() => {
        setSelectedQuestion(null);
        setClosingQuestion(null);
      }, 300);
    } else {
      if (selectedQuestion) {
        setClosingQuestion(selectedQuestion);
        
        setSelectedAnswers(prev => ({
          ...prev,
          [selectedQuestion]: undefined
        }));
        
        setTimeout(() => {
          setSelectedQuestion(questionId);
          setClosingQuestion(null);
        }, 300);
      } else {
        setSelectedQuestion(questionId);
        setClosingQuestion(null);
      }
    }
  };

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const getAnswerStyle = (question: Question, answerIndex: number) => {
    const userAnswer = selectedAnswers[question.id];
    
    if (userAnswer === undefined) {
      return 'glass-interactive-no-active glass-text';
    }
    
    if (answerIndex === question.correctAnswer) {
      return 'glass-success text-emerald-200';
    }
    
    if (userAnswer === answerIndex && answerIndex !== question.correctAnswer) {
      return 'glass-danger text-rose-200';
    }
    
    return 'glass-interactive-no-active glass-text';
  };

  if (!query.trim()) {
    return (
      <div 
        key="empty-query"
        className="flex-1 flex items-center justify-center px-4"
      >
        <div className="text-center">
          <Search className="h-12 w-12 glass-text-muted mx-auto mb-4 opacity-30" />
          <p className="glass-text-secondary mb-2">
            Поиск по вопросам
          </p>
          <p className="text-sm glass-text-muted">
            Введите ключевые слова для поиска
          </p>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div 
        key={`no-results-${query}`}
        className="flex-1 flex items-center justify-center px-4"
      >
        <div className="text-center">
          <Search className="h-8 w-8 glass-text-muted mx-auto mb-2 opacity-50" />
          <p className="text-sm glass-text-secondary">
            По запросу "{query}" ничего не найдено
          </p>
          <p className="text-xs glass-text-muted mt-1">
            Попробуйте изменить ключевые слова
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      key={`results-${results.length}-${results[0]?.id || 'empty'}`}
      className="space-y-3 sm:space-y-4 md:space-y-6 lg:space-y-7 xl:space-y-8 landscape:space-y-2"
    >
      {results.map((question, index) => {
        const isExpanded = selectedQuestion === question.id;
        const userAnswer = selectedAnswers[question.id];
        
        return (
          <div key={question.id} className="rounded-xl overflow-hidden">
            <div 
              className="p-3 sm:p-4 md:p-6 lg:p-7 xl:p-8 landscape-compact-p-1 landscape:p-3 cursor-pointer glass-interactive-no-active rounded-xl"
              onClick={() => toggleQuestionDetails(question.id)}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(question.id);
                }}
                className="p-2 sm:p-3 lg:p-4 xl:p-5 landscape-compact-p-1 landscape:p-2 glass-interactive rounded-lg glass-text"
                style={{ 
                  float: 'right',
                  marginLeft: '0.75rem',
                  position: 'relative',
                  zIndex: 10,
                  transition: 'none'
                }}
              >
                {favorites.has(question.id) ? 
                  <Star className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 landscape:h-3 landscape:w-3 fill-yellow-400 text-yellow-400" /> : 
                  <StarOff className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 landscape:h-3 landscape:w-3 glass-icon" />
                }
              </button>
              
              <div>
                <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 mb-2 sm:mb-3 lg:mb-4 landscape-compact-gap-1 landscape:mb-1">
                  <span className={`text-xs px-2 py-1 rounded-full border backdrop-blur-sm whitespace-nowrap ${getCategoryColor(question.category)}`}>
                    {question.category}
                  </span>
                </div>
                
                <p 
                  className="mb-2 sm:mb-3 lg:mb-4 xl:mb-5 landscape:mb-1 glass-text"
                  style={{ 
                    fontSize: `${actualQuestionSize}px`,
                    fontFamily: settings.fontFamily,
                    lineHeight: '1.5'
                  }}
                >
                  {question.question}
                </p>
              </div>
            </div>

            {(isExpanded || closingQuestion === question.id) && (
              <div 
                className={`border-t border-white/20 p-3 sm:p-4 md:p-6 lg:p-7 xl:p-8 landscape-compact-p-1 landscape:p-3 glass-interactive-no-active rounded-b-xl ${
                  closingQuestion === question.id ? 'question-details-exit' : 'question-details-enter'
                }`}
              >
                <div className="space-y-2 sm:space-y-3 lg:space-y-4 xl:space-y-5 landscape:space-y-1 mb-3 sm:mb-4 md:mb-6 lg:mb-7 xl:mb-8 landscape:mb-2">
                  {question.options.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => handleAnswerSelect(question.id, optionIndex)}
                      disabled={userAnswer !== undefined}
                      className={`w-full p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6 landscape-compact-p-1 landscape:p-2 rounded-lg border-2 backdrop-blur-sm answer-option ${getAnswerStyle(question, optionIndex)}`}
                      style={{ transition: 'none' }}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-5 xl:gap-6 landscape:gap-2">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 xl:w-9 xl:h-9 landscape:w-4 landscape:h-4 rounded-full border-2 border-current flex items-center justify-center text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl landscape-compact-text-xs landscape:text-xs font-medium">
                          {optionIndex + 1}
                        </div>
                        <span
                          className="flex-1 text-left"
                          style={{ 
                            fontSize: `${actualAnswerSize}px`,
                            fontFamily: settings.fontFamily,
                            lineHeight: '1.5'
                          }}
                        >
                          {option}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {question.explanation && (
                  <div className="flex justify-center action-button-enter">
                    <button
                      onClick={() => {
                        setCurrentExplanationQuestion(question);
                        setShowExplanation(true);
                      }}
                      className="px-4 py-2 sm:px-5 sm:py-3 md:px-6 md:py-3 text-button-glass glass-text text-sm sm:text-base md:text-lg"
                    >
                      Пояснение
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <Dialog open={showExplanation} onOpenChange={setShowExplanation}>
        <DialogContent className="max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl mx-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">Пояснение</DialogTitle>
          </DialogHeader>
          <div className="py-4 lg:py-6 xl:py-8">
            {currentExplanationQuestion?.explanation && (
              <p 
                className="leading-relaxed"
                style={{ 
                  fontSize: `${actualAnswerSize}px`,
                  fontFamily: settings.fontFamily,
                  lineHeight: '1.65'
                }}
              >
                {currentExplanationQuestion.explanation}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};